import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EmailStatus } from '@crm/shared';
import { api } from '../../api/client';

export interface Settings {
  companyName: string;
  defaultCurrency: string;
  timezone: string;
  dateFormat: string;
  dedupe: { autoMergeExactEmail: boolean; fuzzyNameThreshold: number; foldGmailDots: boolean };
  verification: { requireVerifiedBeforeCampaign: boolean; builtinOnCreate: boolean };
  /** Sidebar header text when there is no logo; empty = the company name */
  brandTitle: string;
  brandSubtitle: string;
  brandLogoFileId: string | null;
  brandFaviconFileId: string | null;
  /** email statuses campaigns send to; null = DEFAULT_SEND_STATUSES */
  campaignSendStatuses: EmailStatus[] | null;
  /** Optional modules; a switched-off one disappears from the app (Settings → Features) */
  features: Record<FeatureKey, boolean>;
  /** Every feature once, in the order of Settings → Features; the sidebar menu follows it */
  featureOrder: FeatureKey[];
  /** Notification emails to users; `email` switches all of them off at once */
  notifications: { email: boolean; taskAssigned: boolean; taskCompleted: boolean; domainPaused: boolean };
}

export type FeatureKey = 'campaigns' | 'forms' | 'pipelines' | 'inbox' | 'tasks' | 'cardScanner';

export type BrandImage = 'logo' | 'favicon';

const KEY = ['settings'];

export function useSettings() {
  return useQuery({ queryKey: KEY, queryFn: async () => (await api.get<Settings>('/settings')).data });
}

/**
 * Which optional modules are on. `enabled()` is false until settings load, so a switched-off feature never flashes
 * into view; `ready` tells "still loading" apart from "off". `rank()` is a feature's place in the chosen order.
 */
export function useFeatures() {
  const settings = useSettings();
  const features = settings.data?.features;
  const order = settings.data?.featureOrder ?? [];
  return { ready: !!features, enabled: (key: FeatureKey) => !!features?.[key], order, rank: (key: FeatureKey) => order.indexOf(key) };
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Settings>) => (await api.put<Settings>('/settings', input)).data,
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}

/** Uploads (a File) or removes (null) the sidebar logo or the favicon; the API resizes uploads. */
export function useBrandImage(kind: BrandImage) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File | null) => {
      if (!file) return (await api.delete<Settings>(`/settings/${kind}`)).data;
      const form = new FormData();
      form.append('file', file);
      return (await api.post<Settings>(`/settings/${kind}`, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
    },
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}
